import * as d3 from 'd3';
import { hierarchy, HierarchyNode, tree } from 'd3-hierarchy';
import React, { useEffect, useRef, useState } from 'react';
import { getAccessToken } from 'src/utils/token-service';
import * as XLSX from 'xlsx'; // Import the xlsx library
import './dashboard.css';
// import defaultimage from '../../../assets/images/img.png';
import { useOpportunityAnalyticsContext } from '..';
import download from '../../../assets/images/download.png';
import image3 from '../../../assets/images/image.png';
import image1 from '../../../assets/images/img.png';
interface TreeNode {
	name: string;
	value?: number;
	hidden?: boolean;
	children?: TreeNode[];
}

const Dashboard: React.FC = () => {
	// const [startDate, setStartDate] = useState('');
	// const [endDate, setEndDate] = useState('');
	const { startDate, setStartDate, endDate, setEndDate } = useOpportunityAnalyticsContext()
	const [treeData, setTreeData] = useState<TreeNode | null>(null);
	const [error, setError] = useState<string | null>(null);
	const svgRef = useRef<HTMLDivElement | null>(null);
	const [isLoading, setIsLoading] = useState(false); // Track loading state

	const [parentIntent, setParentIntent] = useState('Billing');
	const [mappingType, setMappingType] = useState('customer_intent');

	const apiUrl = 'https://api-dv.brightspeed.com/genai-oa-service/get_bifurcation';


	useEffect(() => {
		const currentDate = new Date();
		// currentDate.setDate(currentDate.getDate() - 1);
		const endDateString = currentDate.toISOString().split('T')[0];
		currentDate.setDate(currentDate.getDate() - 7);
		const startDateString = currentDate.toISOString().split('T')[0];

		setStartDate(startDateString);
		setEndDate(endDateString);
		fetchData(startDateString, endDateString, parentIntent, mappingType);
	}, []);

	const initializeTree = (node: TreeNode, depth: number): TreeNode => {
		if (depth > 3) node.hidden = true;
		if (node.children) {
			node.children = node.children.map((child) => initializeTree(child, depth + 1));
		}
		return node;
	};

	const fetchData = async (start: string, end: string, intent: string, mapping: string) => {
		try {
			setError(null);
			setIsLoading(true);

			if (!start || !end) {
				setError('Start date and end date are required.');
				setIsLoading(false);
				return;
			}

			const url = `${apiUrl}?mapping_type=${mapping}&from_date=${start}&to_date=${end}&parent_intent=${intent}&top_primary_cluster=3`;
			const accessToken = await getAccessToken();
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${accessToken}`,
				},
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(`API Error: ${errorData.message || response.statusText}`);
			}

			const data = await response.json();
			const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
			setTreeData(initializeTree(parsedData, 1));
		} catch (err: any) {
			console.error('Error fetching data:', err);
			setError('Data not available for selected filters.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleNodeClick = (nodeData: HierarchyNode<TreeNode>) => {
		if (nodeData.data.children) {
			nodeData.data.children.forEach((child: TreeNode) => {
				child.hidden = !child.hidden;
			});
			renderTree();
		}
	};

	const renderTree = () => {
		if (!treeData) return;

		const margin = { top: 20, right: 100, bottom: 20, left: 100 };
		const width = 1200 - margin.left - margin.right;
		const height = 600 - margin.top - margin.bottom;

		d3.select(svgRef.current).selectAll('*').remove();

		const svg = d3
			.select(svgRef.current)
			.append('svg')
			.attr('width', width + margin.left + margin.right)
			.attr('height', height + margin.top + margin.bottom)
			.append('g')
			.attr('transform', `translate(${margin.left},${margin.top})`);

		const treeLayout = tree<TreeNode>().size([height, width * 0.8]);

		const filteredRoot = hierarchy<TreeNode>(treeData, (d) => (d.hidden ? null : d.children));

		treeLayout(filteredRoot);
		const nodes = filteredRoot.descendants();
		const links = filteredRoot.links();

		// Render links
		svg
			.selectAll('path.link')
			.data(links)
			.enter()
			.append('path')
			.attr(
				'd',
				d3
					.linkHorizontal()
					.x((d: any) => d.y)
					.y((d: any) => d.x)
			)
			.style('stroke', '#ccc')
			.style('stroke-width', '1.5px')
			.style('fill', 'none');

		const node = svg
			.selectAll('g.node')
			.data(nodes)
			.enter()
			.append('g')
			.attr('class', 'node')
			.attr('transform', (d: any) => `translate(${d.y},${d.x})`)
			.on('click', (_event: MouseEvent, d: HierarchyNode<TreeNode>) => {
				if (d.children) {
					handleNodeClick(d);
				}
			})
			.on('mouseover', (event: any, d: HierarchyNode<TreeNode>) => {
				if (d.depth > 2 && d.data.name === 'Autopay') {
					// Only trigger the tooltip for 'Autopay' node
					// Show image and text on hover
					const nodeData = d.data;
					let imageUrl;
					let text;

					// Map node data to specific images
					if (nodeData.name === 'Autopay') {
						imageUrl = image1; // Use image1 for Autopay
						text = `The volume of calls related to Autopay increased considerably between Dec 13 and Dec 20, with the most noticeable increase in calls related to "Update payment method".`;
					} else {
						imageUrl = image3; // Default image
						text = `This is the sample text message`;
					}

					// Create a container for the image and text
					const tooltip = d3
						.select(svgRef.current)
						.append('div')
						.attr('class', 'node-tooltip')
						.style('top', `${event.pageY}px`)
						.style('left', `${event.pageX + 10}px`);

					tooltip
						.append('img')
						.attr('src', imageUrl)
						.attr('width', '200px')
						.attr('height', '200px')
						.style('margin-bottom', '15px');

					tooltip.append('p').text(text);

					// Add the 'show' class to trigger the fade-in effect
					tooltip.classed('show', true);
				}
			})
			.on('mouseout', () => {
				// Remove the tooltip when mouse leaves the node
				d3.select(svgRef.current).select('.node-tooltip').remove();
			});

		node
			.append('circle')
			.attr('r', 5)
			.style('fill', (d: any) => (d.children ? '#007bff' : '#FEA889'));

		node
			.append('text')
			.attr('x', 10)
			.attr('dy', 4)
			.style('font-size', '10px')
			.text((d: any) => `${d.data.name} (${d.data.value || 'N/A'})`)
			.style('pointer-events', 'none');
	};

	const handleDownloadExcel = () => {
		if (!treeData) return;
		const flattenTreeData = (node: TreeNode, parentName: string = ''): any[] => {
			const flatData = [
				{
					ParentNode: parentName,
					ChildNode: node.name,
					Value: node.value || 'N/A',
					Hidden: node.hidden ? 'Yes' : 'No',
				},
			];

			if (node.children) {
				node.children.forEach((child) => {
					flatData.push(...flattenTreeData(child, node.name));
				});
			}

			return flatData;
		};

		const flatData = flattenTreeData(treeData);
		const ws = XLSX.utils.json_to_sheet(flatData);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Tree Data');
		XLSX.writeFile(wb, 'treeData.xlsx');
	};

	useEffect(() => {
		renderTree();
	}, [treeData]);

	return (
		<div className="dashboard-container">
			<h1 className="dashboard-header">Parent Intent Distribution</h1>

			<div className="date-filter-container">
				<label className="date-filter-label">
					Start Date:
					<input
						type="date"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						className="date-filter-input"
					/>
				</label>
				<label className="date-filter-label">
					End Date:
					<input
						type="date"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						className="date-filter-input"
					/>
				</label>
				<label className="date-filter-label">
					Parent Intent:
					<select className="dropdown-filter" value={parentIntent} onChange={(e) => setParentIntent(e.target.value)}>
						<option value="Billing">Billing</option>
						<option value="Payments">Payments</option>
						<option value="Account Management">Account Management</option>
						<option value="Technical Support">Technical Support</option>
					</select>
				</label>

				<label className="date-filter-label">
					Mapping Type:
					<select className="dropdown-filter" value={mappingType} onChange={(e) => setMappingType(e.target.value)}>
						<option value="customer_intent">Customer Intent</option>
						<option value="problem_identified">Problem Identified</option>
						<option value="root_cause">Root Cause</option>
					</select>
				</label>

				<button onClick={() => fetchData(startDate, endDate, parentIntent, mappingType)} className="filter-button">
					Apply Filter
				</button>

				{/* Show the download icon only when data is available */}
				{treeData && !error && (
					<button onClick={handleDownloadExcel} className="download-button">
						Export Excel
						<img src={download} alt="Download" className="download-img" onClick={handleDownloadExcel} />
					</button>
				)}
			</div>

			{error && <p className="error-message">{error}</p>}

			{/* Loading Spinner Section */}
			{isLoading && (
				<div className="loading-spinner-wrapper">
					<div className="loading-spinner">{/* CSS Spinner */}</div>
				</div>
			)}

			{/* Render Chart Only When Data Is Available and Not Loading */}
			{!isLoading && treeData && !error && (
				<div ref={svgRef} className="tree-container"></div>
			)}
		</div>
	);
};

export default Dashboard;
